/**
 * Меню кабинета: в шапке три рабочих пункта, остальное — под «Настройки ▾».
 *
 * Сервер по-прежнему отдаёт права по разделам (`profile.sections`), меняется только
 * место. Близкие разделы стали одним пунктом: курьеры — фильтр по роли в
 * пользователях, закрепления — вкладка в локациях, интеграции и SLO — мониторинг.
 * Флаги и ручной ввод из интерфейса убраны: заказы приходят только из интеграции,
 * импорт CSV — аварийный канал в настройках. Карта скрыта из меню, пока у кабинета
 * нет карты (Б-01); адрес `/live_map` открывается как раньше.
 *
 * Старые адреса не ломаются: `redirectFor` переводит их на новое место.
 */

export interface MenuEntry {
  /** Код пункта: совпадает с разделом сервера или новый (`monitoring`). */
  readonly code: string;
  /** Разделы сервера, из которых собран пункт; первый — вкладка по умолчанию. */
  readonly tabs: readonly string[];
}

export const PRIMARY_MENU: readonly MenuEntry[] = [
  { code: 'flow', tabs: ['flow'] },
  { code: 'resolutions', tabs: ['resolutions'] },
  { code: 'runs', tabs: ['runs'] },
];

/**
 * «Настройки ▾». У «Пользователей» два раздела сервера: список с правкой отделов
 * (`users`, администратор) и курьеры (`drivers`, логист) — экран один, с фильтром роли.
 */
export const SETTINGS_MENU: readonly MenuEntry[] = [
  { code: 'users', tabs: ['users', 'drivers'] },
  { code: 'locations', tabs: ['locations', 'pickup_matrix'] },
  { code: 'zones', tabs: ['zones'] },
  { code: 'parameters', tabs: ['parameters'] },
  { code: 'templates', tabs: ['templates'] },
  { code: 'audit', tabs: ['audit'] },
  { code: 'import', tabs: ['import'] },
  { code: 'monitoring', tabs: ['integrations', 'slo'] },
];

/** Пункты, где разделы сервера — не вкладки, а один экран. */
export const SINGLE_SCREEN: ReadonlySet<string> = new Set(['users', 'monitoring']);

/** Разделы, которые стали фильтром или частью другого экрана. */
const FOLDED: Readonly<Record<string, { section: string; params: Record<string, string> }>> = {
  drivers: { section: 'users', params: { role: 'driver' } },
  feature_flags: { section: 'parameters', params: {} },
  returns: { section: 'flow', params: { chip: 'attention' } },
};

/** Пункты, у которых пользователю доступна хотя бы одна вкладка. */
export function visibleEntries(
  entries: readonly MenuEntry[],
  sections: readonly string[],
): MenuEntry[] {
  return entries
    .map((entry) => ({ ...entry, tabs: entry.tabs.filter((tab) => sections.includes(tab)) }))
    .filter((entry) => entry.tabs.length > 0);
}

/** Пункт меню, к которому относится адрес; `null` — раздел вне меню (карта, `t`). */
export function entryFor(code: string, sections: readonly string[]): MenuEntry | null {
  return (
    visibleEntries([...PRIMARY_MENU, ...SETTINGS_MENU], sections).find(
      (entry) => entry.code === code || entry.tabs.includes(code),
    ) ?? null
  );
}

/**
 * Куда вести старый адрес. `null` — адрес уже новый, переходить не нужно.
 *
 * `/drivers` → `/users?role=driver`, `/slo` → `/monitoring?tab=slo`, `/feature_flags` →
 * `/parameters`. Вкладка-раздел без собственного пункта меню переводится на пункт,
 * сохраняя вкладку.
 */
export function redirectFor(
  section: string,
  sections: readonly string[],
): { section: string; params: Record<string, string> } | null {
  const folded = FOLDED[section];

  if (folded !== undefined && sections.includes(section)) {
    return folded;
  }

  const entry = entryFor(section, sections);

  if (entry === null || entry.code === section) {
    return null;
  }

  return { section: entry.code, params: { tab: section } };
}
